package com.patronat.top.features.venues.presentation

import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.viewinterop.WebElementView
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.CancellationException
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLScriptElement
import org.w3c.dom.events.Event
import kotlin.js.Promise

@OptIn(ExperimentalComposeUiApi::class)
@Composable
internal actual fun MapLibreVenueMap(
    selectedLatitude: Double?,
    selectedLongitude: Double?,
    mapLibreConfig: MapLibreConfig,
    venueMarkers: List<VenueMapMarker>,
    onPointSelected: (latitude: Double, longitude: Double) -> Unit,
    onMarkerClicked: (VenueMapMarker) -> Unit,
    modifier: Modifier,
    zoom: Double,
    interactive: Boolean
) {
    val latestOnPointSelected by rememberUpdatedState(onPointSelected)
    val latestOnMarkerClicked by rememberUpdatedState(onMarkerClicked)

    var mapContainer by remember { mutableStateOf<HTMLDivElement?>(null) }
    var mapInstance by remember { mutableStateOf<MapLibreMapHandle?>(null) }
    var isMapReady by remember { mutableStateOf(false) }
    val markerInstances = remember { mutableListOf<MapLibreMarkerHandle>() }

    fun resetMapRuntime() {
        clearMarkers(markerInstances)
        disposeMapInstance(mapInstance)
        mapInstance = null
        isMapReady = false
    }

    WebElementView(
        factory = {
            (document.createElement("div") as HTMLDivElement).apply {
                style.borderRadius = "8px"
                style.setProperty("overflow", "hidden")
            }
        },
        modifier = modifier,
        update = { container ->
            container.style.borderRadius = "8px"
            container.style.setProperty("overflow", "hidden")

            if (mapContainer !== container) {
                mapContainer = container
            }
        },
        onRelease = { releasedContainer ->
            if (mapContainer === releasedContainer) {
                resetMapRuntime()
                mapContainer = null
            }
        }
    )

    LaunchedEffect(mapContainer, mapLibreConfig.apiKey, mapLibreConfig.style, interactive) {
        val container = mapContainer ?: return@LaunchedEffect

        resetMapRuntime()

        try {
            loadMapLibreScript()

            val lat = selectedLatitude ?: MapTilerConfiguration.DEFAULT_LATITUDE
            val lng = selectedLongitude ?: MapTilerConfiguration.DEFAULT_LONGITUDE
            val styleUrl = buildStyleUrl(mapLibreConfig)
            val mapOptions = createMapOptions(
                container = container,
                styleUrl = styleUrl,
                longitude = lng,
                latitude = lat,
                zoom = zoom,
                interactive = interactive
            )

            val map = createMapInstance(mapOptions)
            mapInstance = map

            val loadHandler: MapLoadHandler = {
                isMapReady = true
                map.resize()
                console.log("MapLibre GL map loaded successfully")
            }
            map.on("load", loadHandler)
            setTempProperty(map, MAP_LOAD_HANDLER_KEY, loadHandler)

            val clickHandler: MapClickHandler = { event ->
                val lngValue = dynamicToDouble(event?.lngLat?.lng)
                val latValue = dynamicToDouble(event?.lngLat?.lat)

                if (lngValue != null && latValue != null) {
                    latestOnPointSelected(latValue, lngValue)
                }
            }
            if (interactive) {
                map.on("click", clickHandler)
                setTempProperty(map, MAP_CLICK_HANDLER_KEY, clickHandler)
            }
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            console.error("Failed to initialize MapLibre GL", e)
        }
    }

    LaunchedEffect(mapInstance, isMapReady, venueMarkers, interactive) {
        val map = mapInstance ?: return@LaunchedEffect
        if (!isMapReady) return@LaunchedEffect

        clearMarkers(markerInstances)

        venueMarkers.forEach { marker ->
            val markerInstance = createMarkerInstance(
                marker = marker,
                mapInstance = map,
                interactive = interactive,
                onMarkerClicked = { latestOnMarkerClicked(marker) }
            )
            markerInstances.add(markerInstance)
        }
    }

    LaunchedEffect(mapInstance, isMapReady, selectedLatitude, selectedLongitude) {
        val map = mapInstance ?: return@LaunchedEffect
        if (!isMapReady) return@LaunchedEffect
        if (selectedLatitude == null || selectedLongitude == null) return@LaunchedEffect

        map.setCenter(arrayOf(selectedLongitude, selectedLatitude))
    }

    LaunchedEffect(mapInstance, isMapReady, zoom) {
        val map = mapInstance ?: return@LaunchedEffect
        if (!isMapReady) return@LaunchedEffect

        map.setZoom(zoom)
    }

    DisposableEffect(Unit) {
        onDispose {
            resetMapRuntime()
            mapContainer = null
        }
    }
}

internal actual fun isMapLibreSupported(): Boolean = true

internal actual suspend fun searchMapTilerAddresses(
    query: String,
    apiKey: String,
    proximity: Pair<Double, Double>?,
    countryCode: String,
    limit: Int
): List<MapTilerAddressResult> {
    if (query.isBlank()) return emptyList()

    return try {
        val proximityParam = if (proximity != null) {
            "&proximity=${proximity.second},${proximity.first}"
        } else ""

        val url = "https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json" +
                "?key=${apiKey}" +
                "&country=${countryCode}" +
                "&limit=${limit}" +
                proximityParam

        val response = window.fetch(url).await()
        val json = response.json().await()

        val features = json.asDynamic().features as Array<dynamic>

        features.map { feature ->
            val coordinates = feature.geometry.coordinates as Array<Double>
            MapTilerAddressResult(
                id = feature.id as String,
                placeName = feature.place_name as String,
                latitude = coordinates[1],
                longitude = coordinates[0],
                relevance = (feature.relevance as? Double) ?: 1.0,
                placeType = (feature.place_type as Array<String>).toList()
            )
        }.toList()

    } catch (e: Exception) {
        console.error("MapTiler geocoding failed", e)
        listOf(
            MapTilerAddressResult(
                id = "mock.1",
                placeName = "$query, Київ, Україна",
                latitude = 50.4501,
                longitude = 30.5234,
                relevance = 0.9,
                placeType = listOf("place")
            )
        )
    }
}

internal actual suspend fun reverseMapTilerGeocode(
    latitude: Double,
    longitude: Double,
    apiKey: String
): MapTilerAddressResult? {
    return try {
        val url = "https://api.maptiler.com/geocoding/${longitude},${latitude}.json" +
                "?key=${apiKey}"

        val response = window.fetch(url).await()
        val json = response.json().await()
        val features = json.asDynamic().features as Array<dynamic>

        if (features.isNotEmpty()) {
            val feature = features[0]
            val coordinates = feature.geometry.coordinates as Array<Double>
            MapTilerAddressResult(
                id = feature.id as String,
                placeName = feature.place_name as String,
                latitude = coordinates[1],
                longitude = coordinates[0],
                relevance = 1.0,
                placeType = (feature.place_type as Array<String>).toList()
            )
        } else {
            MapTilerAddressResult(
                id = "reverse.1",
                placeName = "Київ, Україна",
                latitude = latitude,
                longitude = longitude,
                relevance = 1.0,
                placeType = listOf("place")
            )
        }

    } catch (e: Exception) {
        console.error("MapTiler reverse geocoding failed", e)
        MapTilerAddressResult(
            id = "mock.reverse",
            placeName = "Київ, Україна",
            latitude = latitude,
            longitude = longitude,
            relevance = 1.0,
            placeType = listOf("place")
        )
    }
}

private suspend fun loadMapLibreScript() {
    if (isMapLibreLoaded()) {
        return
    }

    ensureMapLibreCss()

    mapLibreScriptPromise?.let {
        it.await()
        return
    }

    val loadPromise = Promise<Unit> { resolve, reject ->
        if (isMapLibreLoaded()) {
            resolve(Unit)
            return@Promise
        }

        val existingScript = document.querySelector("script[data-maplibre-gl='true']") as? HTMLScriptElement
        if (existingScript != null) {
            existingScript.addEventListener("load", { _: Event -> resolve(Unit) })
            existingScript.addEventListener("error", { _: Event ->
                reject(Exception("Failed to load MapLibre GL script"))
            })
            return@Promise
        }

        val script = document.createElement("script") as HTMLScriptElement
        script.src = MAPLIBRE_SCRIPT_URL
        script.defer = true
        script.setAttribute("data-maplibre-gl", "true")
        script.addEventListener("load", { _: Event ->
            resolve(Unit)
        })
        script.addEventListener("error", { _: Event ->
            reject(Exception("Failed to load MapLibre GL script"))
        })
        document.head?.appendChild(script)
    }

    mapLibreScriptPromise = loadPromise

    try {
        loadPromise.await()
    } catch (e: Throwable) {
        mapLibreScriptPromise = null
        throw e
    }
}

private fun ensureMapLibreCss() {
    val existingCss = document.querySelector("link[data-maplibre-gl='true']") as? HTMLLinkElement
    if (existingCss != null) {
        return
    }

    val cssLink = document.createElement("link") as HTMLLinkElement
    cssLink.rel = "stylesheet"
    cssLink.href = MAPLIBRE_CSS_URL
    cssLink.setAttribute("data-maplibre-gl", "true")
    document.head?.appendChild(cssLink)
}

private fun buildStyleUrl(config: MapLibreConfig): String {
    return "https://api.maptiler.com/maps/${config.style}/style.json?key=${config.apiKey}"
}

private fun createMapOptions(
    container: HTMLDivElement,
    styleUrl: String,
    longitude: Double,
    latitude: Double,
    zoom: Double,
    interactive: Boolean
): dynamic {
    val options = js("({})")
    options.container = container
    options.style = styleUrl
    options.center = arrayOf(longitude, latitude)
    options.zoom = zoom
    options.interactive = interactive
    options.scrollZoom = interactive
    options.doubleClickZoom = false
    options.touchZoomRotate = false
    options.dragRotate = false
    options.keyboard = false
    return options
}

private fun createMapInstance(options: dynamic): MapLibreMapHandle {
    val mapConstructor = js("window.maplibregl.Map")
    return js("Reflect.construct")(mapConstructor, arrayOf(options)).unsafeCast<MapLibreMapHandle>()
}

private fun createMarkerInstance(
    marker: VenueMapMarker,
    mapInstance: MapLibreMapHandle,
    interactive: Boolean,
    onMarkerClicked: () -> Unit
): MapLibreMarkerHandle {
    val markerConstructor = js("window.maplibregl.Marker")
    val markerInstance = js("Reflect.construct")(markerConstructor, emptyArray<dynamic>())
        .unsafeCast<MapLibreMarkerHandle>()
    markerInstance.setLngLat(arrayOf(marker.longitude, marker.latitude))
    markerInstance.addTo(mapInstance)

    if (interactive) {
        val element = markerInstance.getElement()?.unsafeCast<org.w3c.dom.HTMLElement?>()
        if (element != null) {
            element.style.cursor = "pointer"

            val clickListener: MarkerClickHandler = { event ->
                event.stopPropagation()
                onMarkerClicked()
            }

            element.addEventListener("click", clickListener)
            setTempProperty(markerInstance, MARKER_CLICK_ELEMENT_KEY, element)
            setTempProperty(markerInstance, MARKER_CLICK_HANDLER_KEY, clickListener)
        }
    }

    return markerInstance
}

private fun removeMarker(markerInstance: MapLibreMarkerHandle) {
    val clickElement = getTempProperty(markerInstance, MARKER_CLICK_ELEMENT_KEY)
        ?.unsafeCast<org.w3c.dom.HTMLElement?>()
    val clickListener = getTempProperty(markerInstance, MARKER_CLICK_HANDLER_KEY)
        ?.unsafeCast<MarkerClickHandler?>()

    if (clickElement != null && clickListener != null) {
        clickElement.removeEventListener("click", clickListener)
    }

    markerInstance.remove()
}

private fun clearMarkers(markerInstances: MutableList<MapLibreMarkerHandle>) {
    markerInstances.forEach(::removeMarker)
    markerInstances.clear()
}

private fun disposeMapInstance(mapInstance: MapLibreMapHandle?) {
    if (mapInstance == null) return

    val clickHandler = getTempProperty(mapInstance, MAP_CLICK_HANDLER_KEY)
        ?.unsafeCast<MapClickHandler?>()
    val loadHandler = getTempProperty(mapInstance, MAP_LOAD_HANDLER_KEY)
        ?.unsafeCast<MapLoadHandler?>()

    if (clickHandler != null) {
        try {
            mapInstance.off("click", clickHandler)
        } catch (_: Throwable) {
        }
    }
    if (loadHandler != null) {
        try {
            mapInstance.off("load", loadHandler)
        } catch (_: Throwable) {
        }
    }

    mapInstance.remove()
}

private fun setTempProperty(target: Any, key: String, value: dynamic) {
    target.asDynamic()[key] = value
}

private fun getTempProperty(target: Any, key: String): dynamic {
    return target.asDynamic()[key]
}

private fun isMapLibreLoaded(): Boolean {
    return js("typeof window.maplibregl !== 'undefined'") as Boolean
}

private fun dynamicToDouble(value: dynamic): Double? {
    return when (value) {
        is Double -> value
        is Int -> value.toDouble()
        is Float -> value.toDouble()
        else -> null
    }
}

private external fun encodeURIComponent(str: String): String

private typealias MapLoadHandler = () -> Unit
private typealias MapClickHandler = (dynamic) -> Unit
private typealias MarkerClickHandler = (Event) -> Unit

private external interface MapLibreMapHandle {
    fun on(event: String, listener: dynamic)
    fun off(event: String, listener: dynamic)
    fun resize()
    fun setCenter(center: Array<Double>)
    fun setZoom(zoom: Double)
    fun remove()
}

private external interface MapLibreMarkerHandle {
    fun setLngLat(coordinates: Array<Double>): MapLibreMarkerHandle
    fun addTo(map: MapLibreMapHandle): MapLibreMarkerHandle
    fun getElement(): Any?
    fun remove()
}

private const val MAP_LOAD_HANDLER_KEY = "__topLoadHandler"
private const val MAP_CLICK_HANDLER_KEY = "__topClickHandler"
private const val MARKER_CLICK_ELEMENT_KEY = "__topMarkerClickElement"
private const val MARKER_CLICK_HANDLER_KEY = "__topMarkerClickListener"

private const val MAPLIBRE_SCRIPT_URL = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js"
private const val MAPLIBRE_CSS_URL = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css"
private var mapLibreScriptPromise: Promise<Unit>? = null
